import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const initialAttempt = {
  scores: {
    Safety: 0,
    Service: 0,
    Efficiency: 0,
    Professionalism: 0,
    StressResistance: 0,
  },
  choices: {},
  checklistResults: {},
  errors: 0,
  startTime: null,
};

export const useGameStore = create(
  persist(
    (set, get) => ({
      // Hydration flag
      _hasHydrated: false,
      setHasHydrated: (val) => set({ _hasHydrated: val }),

      // User data
      userName: '',
      isRegistered: false,

      // Current session
      currentModuleId: null,
      currentSceneIndex: 0,

      // Module progress
      moduleProgress: {},

      // Current attempt
      currentAttempt: { ...initialAttempt },

      // Voice enabled
      voiceEnabled: false,

      // Actions
      setUserName: (name) => set({ userName: name, isRegistered: true }),

      toggleVoice: () => set((state) => ({ voiceEnabled: !state.voiceEnabled })),

      startModule: (moduleId) => set({
        currentModuleId: moduleId,
        currentSceneIndex: 0,
        currentAttempt: {
          ...initialAttempt,
          scores: { ...initialAttempt.scores },
          choices: {},
          checklistResults: {},
          errors: 0,
          startTime: Date.now(),
        },
      }),

      makeChoice: (sceneId, choice) => set((state) => {
        const newScores = { ...state.currentAttempt.scores };
        if (choice.scores) {
          Object.entries(choice.scores).forEach(([key, value]) => {
            newScores[key] = (newScores[key] || 0) + value;
          });
        }
        return {
          currentAttempt: {
            ...state.currentAttempt,
            scores: newScores,
            choices: {
              ...state.currentAttempt.choices,
              [sceneId]: choice.id,
            },
            errors: state.currentAttempt.errors + (choice.is_optimal ? 0 : 1),
          },
        };
      }),

      saveChecklistResult: (sceneId, checkedItems, totalRequired) => set((state) => {
        const allChecked = checkedItems >= totalRequired;
        return {
          currentAttempt: {
            ...state.currentAttempt,
            checklistResults: {
              ...state.currentAttempt.checklistResults,
              [sceneId]: { checkedItems, totalRequired, allChecked },
            },
            errors: state.currentAttempt.errors + (allChecked ? 0 : 1),
            scores: {
              ...state.currentAttempt.scores,
              Safety: state.currentAttempt.scores.Safety + (allChecked ? 10 : 3),
            },
          },
        };
      }),

      nextScene: () => set((state) => ({
        currentSceneIndex: state.currentSceneIndex + 1,
      })),

      completeModule: () => set((state) => {
        const timeSeconds = Math.floor((Date.now() - (state.currentAttempt.startTime || Date.now())) / 1000);
        const existingProgress = state.moduleProgress[state.currentModuleId] || { attempts: [] };
        return {
          moduleProgress: {
            ...state.moduleProgress,
            [state.currentModuleId]: {
              status: 'completed',
              completedAt: new Date().toISOString(),
              attempts: [
                ...(existingProgress.attempts || []),
                {
                  date: new Date().toISOString(),
                  scores: { ...state.currentAttempt.scores },
                  errors: state.currentAttempt.errors,
                  timeSeconds,
                },
              ],
            },
          },
          currentModuleId: null,
          currentSceneIndex: 0,
        };
      }),

      resetModule: (moduleId) => set((state) => ({
        moduleProgress: {
          ...state.moduleProgress,
          [moduleId]: {
            ...state.moduleProgress[moduleId],
            status: 'locked',
          },
        },
      })),

      getReadinessStatus: () => {
        const errors = get().currentAttempt.errors;
        if (errors === 0) return { label: '\u041e\u0442\u043b\u0438\u0447\u043d\u043e', color: 'success' };
        if (errors === 1) return { label: '\u0425\u043e\u0440\u043e\u0448\u043e', color: 'success' };
        if (errors === 2) return { label: '\u0423\u0434\u043e\u0432\u043b\u0435\u0442\u0432\u043e\u0440\u0438\u0442\u0435\u043b\u044c\u043d\u043e', color: 'warning' };
        return { label: '\u0422\u0440\u0435\u0431\u0443\u0435\u0442\u0441\u044f \u0434\u043e\u043f. \u043e\u0431\u0443\u0447\u0435\u043d\u0438\u0435', color: 'destructive' };
      },

      getTimeSpent: () => {
        const start = get().currentAttempt.startTime;
        if (!start) return 0;
        return Math.floor((Date.now() - start) / 1000);
      },

      logout: () => {
        set({
          userName: '',
          isRegistered: false,
          currentModuleId: null,
          currentSceneIndex: 0,
          moduleProgress: {},
          currentAttempt: { ...initialAttempt },
        });
      },
    }),
    {
      name: 'delivery-ace-storage',
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.setHasHydrated(true);
        }
      },
      partialize: (state) => ({
        userName: state.userName,
        isRegistered: state.isRegistered,
        currentModuleId: state.currentModuleId,
        currentSceneIndex: state.currentSceneIndex,
        moduleProgress: state.moduleProgress,
        currentAttempt: state.currentAttempt,
        voiceEnabled: false,
      }),
    }
  )
);
